import { z } from "zod";

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function isValidPhone(value) {
  return /^[0-9 ()+-]+$/.test(value);
}

export function createTechnicalSupportSchema(config) {
  return z
    .object({
      firstNameQA: z.string().trim().min(1, "required"),
      surnameQA: z.string().trim().min(1, "required"),
      emailQA: z
        .string()
        .trim()
        .min(1, "required")
        .refine((value) => isValidEmail(value), "email"),
      emailQAConfirm: z
        .string()
        .trim()
        .min(1, "required")
        .refine((value) => isValidEmail(value), "email"),
      phoneQA: z
        .string()
        .trim()
        .min(1, "required")
        .refine((value) => isValidPhone(value), "phone"),
      organisationQA: z.string().trim(),
      organisationIdQA: z.union([z.string(), z.number(), z.null()]).optional(),
      organisationHasValidSelection: z.boolean().optional(),
      otherOrganisationQA: z.string().trim(),
      tandcQA: z.boolean(),
    })
    .superRefine((value, ctx) => {
      if (value.firstNameQA.length > 256) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["firstNameQA"],
          message: "maxLength",
        });
      }

      if (value.surnameQA.length > 256) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["surnameQA"],
          message: "maxLength",
        });
      }

      if (value.emailQA.length > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["emailQA"],
          message: "maxLength",
        });
      }

      if (value.emailQAConfirm.length > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["emailQAConfirm"],
          message: "maxLength",
        });
      }

      if (value.phoneQA.length > 32) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["phoneQA"],
          message: "maxLength",
        });
      }

      if (value.emailQAConfirm !== value.emailQA) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["emailQAConfirm"],
          message: "sameAs",
        });
      }

      if (!config.isGasConsumerQuery && !String(value.organisationQA || "").trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["organisationQA"],
          message: "required",
        });
      } else if (
        !config.isGasConsumerQuery &&
        String(value.organisationQA || "").trim() &&
        value.organisationHasValidSelection === false
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["organisationQA"],
          message: "invalid",
        });
      }

      if (String(value.organisationQA || "").length > 256) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["organisationQA"],
          message: "maxLength",
        });
      }

      if (
        Number(value.organisationIdQA) === -1 &&
        !String(value.otherOrganisationQA || "").trim()
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["otherOrganisationQA"],
          message: "required",
        });
      }

      if (!value.tandcQA) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["tandcQA"],
          message: "required",
        });
      }
    });
}

export function mapZodIssuesToFieldErrors(issues = []) {
  return issues.reduce((acc, issue) => {
    const key = issue.path?.[0];
    if (key && !acc[key]) {
      acc[key] = issue.message;
    }
    return acc;
  }, {});
}
